import { usePress } from '@react-aria/interactions'
import { mergeProps } from '@react-aria/utils'
import { useModals } from 'almanac-ui/Modal'
import { ArrowDown, ArrowUp, Grabber, Pin, Sort, Unpin } from 'almanac-ui/icons/16'
import { useResizeObserver, useThrottledFn } from 'beautiful-react-hooks'
import cx from 'classnames'
import { Command } from 'common/Prose/Commands'
import {
  _putSelectionAtBeginningOfTable,
  addRowAt,
  selectColumn,
  selectRow,
  toggleSelectTable,
} from 'common/Prose/Commands/table'
import {
  _addColumnAt,
  convertRowsToHeader,
  distributeColumns,
  maybeDeleteTable,
  removeHeadersUpToRow,
  resizeHeight,
  resizeWidth,
} from 'common/Prose/Commands/table/mutation'
import { selectBorder } from 'common/Prose/Commands/table/selection'
import { _sortColumn } from 'common/Prose/Commands/table/sortColumn'
import { AlmanacDropCursorPluginMeta } from 'common/Prose/Plugins/AlmanacDropCursor/AlmanacDropCursor'
import { ALMANAC_DROP_CURSOR_PLUGIN_KEY } from 'common/Prose/Plugins/AlmanacDropCursor/constants'
import { TableDragManagerMeta, TableDragManagerPluginKey } from 'common/Prose/Plugins/TableDragManager/TableDragManager'
import { tryGetTrackChangesState } from 'common/Prose/Plugins/TrackChanges/constants'
import { TableContextMenu } from 'common/Prose/components/TableContextMenu/TableContextMenu'
import { useReactNodeView } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { ShortcutAwareTooltip } from 'common/Prose/utils/ShortcutAwareTooltip'
import { LineMode } from 'common/Prose/utils/table'
import { BorderSelection } from 'common/Prose/utils/table/BorderSelection'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { NotificationModal } from 'components/NotificationModal/NotificationModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useStateRef } from 'hooks/useStateRef'
import { dropRight, isEqual, isEqualWith, sum, times } from 'lodash'
import { NodeSelection } from 'prosemirror-state'
import * as React from 'react'
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useEvent, useTimeoutFn } from 'react-use'
import { ViewEditableContext } from 'screens/ProseEdit/components/ViewEditableContextProvider/ViewEditableContextProvider'
import { CellBorder } from './CellBorder'
import { CellResizeHandler } from './CellResizeHandler'
import { CellSelectionIndicator } from './CellSelectionIndicator'
import { ReactComponent as AddIcon } from './Icons/add.svg'
import { ReactComponent as TableIcon } from './Icons/table.svg'
import styles from './Table.module.scss'
import { TableScrollIndicator } from './TableScrollIndicator'
import { rowIsHeaderCells } from './rowIsHeaderCells'

function TableButton(props: {
  onClick: () => void
  onDragStart: (e: React.DragEvent<HTMLElement>) => void
  isTableSelected: boolean
  onPointerLeave: () => void
}): React.ReactElement {
  const { onClick, onDragStart, isTableSelected, onPointerLeave } = props
  const ref = useRef<HTMLDivElement>(null)

  const { pressProps } = usePress({ ref })

  return (
    <div
      ref={ref}
      className={cx(styles.tableHandle, isTableSelected ? styles.active : styles.inactive)}
      onDragStart={onDragStart}
      draggable={true}
      {...mergeProps({ onClick }, { onClick: pressProps.onClick })} // Chain the onClick handlers.
      onPointerUp={pressProps.onPointerDown} // Hack: we want the menu to open on pointerup, not pointerdown.
      onPointerLeave={onPointerLeave}
    >
      <TableIcon />
    </div>
  )
}

export const Table = React.forwardRef<HTMLTableElement, { children?: React.ReactNode }>(
  ({ children }, contentDomRef) => {
    const context = useReactNodeView()
    const { node, view } = context
    const scrollRef = useRef<HTMLDivElement>(null)
    const getPos = context.getPos as () => number
    const selfRef = useRef<HTMLDivElement>(null)
    const handlesRef = useRef<HTMLDivElement>(null)
    const gridColumnSizes = () => [64, ...columnWidths.flatMap((cw) => (cw !== undefined ? [0, cw] : [])), 0, 16]
    const gridRowSizes = () => [
      16,
      ...rowHeights.flatMap((rh) => (rh !== undefined ? [0, rh] : [])),
      0,
      16 + (tableScroll ? 10 : 0),
    ]
    const [tableScroll, setTableScroll] = useState<boolean>(true)
    const [columnWidths, setColumnWidths, _columnWidths] = useStateRef<number[]>([])
    const [rowHeights, setRowHeights, _rowHeights] = useStateRef<number[]>([])
    const [hoveredColumns, setHoveredColumns] = useState<number[]>([])
    const [hoveredRows, setHoveredRows] = useState<number[]>([])
    const [showSnapBorder, setShowSnapBorder] = useState<boolean>(false)
    const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false)
    const [hoveringControls, setHoveringControls] = useState<boolean>(false)
    const isTableSelected = view?.state.selection instanceof NodeSelection && view?.state.selection.from === getPos()
    let [tableMap, setTableMap] = useState<TableMap>()
    const [, setRerender] = useStateRef(0)
    const isMouseOver = useRef(false)
    const { openModal } = useModals()

    const trackChangesState = tryGetTrackChangesState(view.state)
    const trackingChanges: boolean = trackChangesState?.store.trackingChanges || false

    const containerDOMRect = useResizeObserver(selfRef)
    const { isMobile } = useResponsiveDesign()
    const { editable } = useContext(ViewEditableContext) ?? { editable: process.env.NODE_ENV === 'test' ? true : false }
    const showControls = view?.editable && !isMobile

    const dense_table = useFlipperFlag('dense_table')

    const openCantSortModal = () => {
      openModal(
        NotificationModal,
        {
          title: 'There was a problem',
          message: 'You cannot sort a table with vertically merged cells. Please unmerge the cells and try again.',
        },
        { newStack: false },
      )
    }

    const isHoveringControls = () => {
      setHoveringControls(true)
    }

    const isNotHoveringControls = () => {
      setHoveringControls(false)
    }

    const getSelectedCells = () => {
      if (view) {
        const isCellSelection = view.state.selection instanceof CellSelection
        if (!tableMap) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const newTableMap = new TableMap({ posBeforeTable: getPos(), node: node! })
          tableMap = newTableMap
          setTableMap(newTableMap)
        }
        if (isCellSelection) {
          const selection = view.state.selection as unknown as CellSelection
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const selectedCoords = selection.$includedCells.map((rp) => tableMap?.getCoordListOfPos(rp.pos)!).flat(1)

          return selectedCoords
        }
      }
      return []
    }

    useEffect(() => {
      // TODO: This really should be a command on /home/nate/Almanac/almanac-editor/src/common/Prose/Plugins/Keymap.ts Delete
      const confirmTableDelete = (event: KeyboardEvent) => {
        if (view && (event.key === 'Backspace' || event.key === 'Delete')) {
          if (isTableSelected) {
            event.preventDefault()
            maybeDeleteTable(view)
            return false
          }
          // Do not attempt to delete content from here without double checking comments and other floating edits
          // see https://linear.app/almanac/issue/REV-1051
          // https://github.com/almanaclabs/almanac-editor/pull/8321 onKeyDown
          // https://github.com/almanaclabs/almanac-editor/pull/8611 plugins of Article
          // and https://linear.app/almanac/issue/RAD-2470 this
        }
      }

      if (view?.editable) {
        window.addEventListener('keydown', confirmTableDelete, true)

        return () => window.removeEventListener('keydown', confirmTableDelete, true)
      }
    }, [view, isTableSelected])

    const [selectedCells, setSelectedCells, _selectedCells] = useStateRef(getSelectedCells())
    useEffect(() => {
      recomputeSelectedCells()
    })

    const throttledFn = useThrottledFn(
      (e: Event) => {
        setTimeout(() => {
          // NOTICE: The following had a setImmediate now replaced by throttle + setTimeout
          // prosemirror only updates the selection way later (later than a few frames, like 5-10)
          setRerender(Math.random())
        }, 100)
      },
      200,
      { leading: true, trailing: true },
      [],
    )

    useEvent('selectionchange', throttledFn, document)
    useEvent('click', throttledFn, document)

    useEffect(() => {
      if (node && !isNaN(getPos())) {
        const pos = getPos()
        const newTableMap = new TableMap({ posBeforeTable: pos, node: node })
        setTableMap(newTableMap)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getPos, getPos(), node])

    // setTableScroll will come back the same value so should not cause infinite chain of updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
      throttledComputeDimensions()
      const tableEl = (contentDomRef as React.RefObject<HTMLTableElement>).current
      if (tableEl && containerDOMRect?.width) {
        setTableScroll(tableEl.offsetWidth > containerDOMRect?.width)
      }
    })

    const lastRowHovered = useMemo(() => {
      if (!tableMap) return false
      return hoveredRows.length > 0 && hoveredRows[hoveredRows.length - 1] === tableMap.numRows - 1
    }, [hoveredRows, tableMap])

    const lastColumnHovered = useMemo(() => {
      if (!tableMap) return false
      return hoveredColumns.length > 0 && hoveredColumns[hoveredColumns.length - 1] === tableMap.numCols - 1
    }, [hoveredColumns, tableMap])

    /**
     * This effect is used to distribute the columns so that they fit the container width
     * - It will *not* be called when the table is first inserted
     * - It will *not* be called if the table has been manually resized
     * - It will *not* be called if the table is already the same width as the container
     * - it *will* be called if the container width changes, such as when:
     *  - reducing the size of the window (for fixed mode)
     *  - opening/closing sidebars
     *  - changing to full-width mode and expanding the window (e.g. on a widescreen monitor)
     */
    useLayoutEffect(() => {
      const tableElementWidth = selfRef.current?.querySelector('table')?.getBoundingClientRect().width
      const containerWidth = containerDOMRect?.width
      const hasTableBeenResized = node.attrs.resizedColumns.length > 0
      // since our dependency array includes things that might change, but that we don't want to react
      // to (e.g. `getPos` changed - we don't care, but we need to keep the up-to-date getPos in this
      // closure), we only want to run this effect when `tableElementWidth` and `containerWidth` are
      // both defined and different from each other, and the table has not been manually resized
      if (
        editable &&
        tableElementWidth &&
        containerWidth &&
        tableElementWidth !== containerWidth &&
        !hasTableBeenResized
      ) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        distributeColumns(containerDOMRect!.width, getPos())(view!.state, view!.dispatch)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerDOMRect?.width, getPos, node.attrs.resizedColumns.length, view, editable])

    const computeDimensions = () => {
      if (handlesRef.current && tableMap) {
        const tableEl = handlesRef.current.parentElement?.nextElementSibling
        if (tableEl) {
          const rows = Array.from(tableEl.querySelectorAll('tr'))
          if (rows.length) {
            const computedRowHeights = rows.map((row) => {
              const rowHeight = row.getBoundingClientRect().height
              if (rowHeight < 1) return 0
              return rowHeight
            })

            const computedColumnWidths = tableMap.getColumnWidths()

            isEqual(computedRowHeights, _rowHeights.current) || setRowHeights(computedRowHeights)
            isEqual(computedColumnWidths, _columnWidths.current) || setColumnWidths(computedColumnWidths as number[])
          }
        }
      }
    }

    const throttledComputeDimensions = useThrottledFn(computeDimensions, 100, { leading: true, trailing: true })

    const recomputeSelectedCells = () => {
      const cells = getSelectedCells()
      const isSame = isEqualWith(_selectedCells.current, cells, (a, b, i) => {
        if (i !== undefined) {
          return a[0] === b[0] && a[1] === b[1]
        }
      })
      if (!isSame) {
        setSelectedCells(cells)
      }
    }

    const hideControls = () => {
      if (hoveringControls) return
      setHoveredColumns([])
      setHoveredRows([])
      handlesRef?.current?.querySelector(`.${styles.tableHandle}`)?.classList.remove(styles.hover)
    }
    const onMouseEnter = () => {
      isMouseOver.current = true
    }
    const onMouseLeave = () => {
      isMouseOver.current = false
      hideControls()
    }

    const [, , resetTimeout] = useTimeoutFn(hideControls, 1000)

    const handleCellHover = (e: MouseEvent) => {
      if (!isMouseOver.current) return
      const tableEl = (contentDomRef as React.RefObject<HTMLTableElement>).current
      const handleEl = handlesRef.current
      const { clientX, clientY } = e
      if (tableEl == null || tableMap == null || handleEl == null || clientX == null || clientY == null) return

      // look for multiple columnButtonsContainers and show only the closest to mouse
      const visibleButtonContainers = document.querySelectorAll(`.${styles.columnButtonsContainer}`)
      if (visibleButtonContainers.length > 0 && !hoveringControls) {
        const closestButtonContainer = Array.from(visibleButtonContainers).reduce((prev, curr) =>
          Math.abs(curr.getBoundingClientRect().left - clientX) < Math.abs(prev.getBoundingClientRect().left - clientX)
            ? curr
            : prev,
        )
        visibleButtonContainers.forEach((el) => {
          if (el !== closestButtonContainer) {
            el.classList.add(styles.hide)
          } else {
            el.classList.remove(styles.hide)
          }
        })
      }

      // look for multiple rowButtonsContainers and show only the closest to mouse
      const visibleRowButtonContainers = document.querySelectorAll(`.${styles.rowButtonsContainer}`)
      if (visibleRowButtonContainers.length > 0 && !hoveringControls) {
        const closestButtonContainer = Array.from(visibleRowButtonContainers).reduce((prev, curr) =>
          Math.abs(curr.getBoundingClientRect().top - clientY) < Math.abs(prev.getBoundingClientRect().top - clientY)
            ? curr
            : prev,
        )

        visibleRowButtonContainers.forEach((el) => {
          if (el !== closestButtonContainer) {
            el.classList.add(styles.hide)
          } else {
            el.classList.remove(styles.hide)
          }
        })
      }

      const tableAffordanceHoverX = 100
      const tableAffordanceHoverY = 100
      const { top, left } = handleEl.getBoundingClientRect()
      if (clientY - top < tableAffordanceHoverY && clientX - left < tableAffordanceHoverX) {
        handleEl.querySelector(`.${styles.tableHandle}`)?.classList.add(styles.hover)
      } else {
        handleEl.querySelector(`.${styles.tableHandle}`)?.classList.remove(styles.hover)
      }
      const cellsEls = tableEl.querySelectorAll('td,th')
      const foundCell = document.elementFromPoint(clientX, clientY)?.closest('td,th')
      if (foundCell) {
        const foundCellIdx = Array.from(cellsEls).findIndex((cellEl) => cellEl === foundCell)
        const hoveredCoords = tableMap.getCoordListOfCellIndex(foundCellIdx) // [row, col][]
        const newHoveredColumns = hoveredCoords.map(([, col]) => col).sort()
        const newHoveredRows = hoveredCoords.map(([row]) => row).sort()
        if (!isEqual(hoveredColumns, newHoveredColumns)) setHoveredColumns(newHoveredColumns)
        if (!isEqual(hoveredRows, newHoveredRows)) setHoveredRows(newHoveredRows)
        resetTimeout()
      }
    }
    const throttledHandleCellHover = useThrottledFn(handleCellHover, 100, { leading: true, trailing: true })

    useLayoutEffect(() => {
      const tableEl = (selfRef as React.RefObject<HTMLTableElement>).current
      if (tableMap && tableEl) {
        tableEl.addEventListener('mousemove', throttledHandleCellHover)
        return () => {
          tableEl.removeEventListener('mousemove', throttledHandleCellHover)
        }
      }
    }, [selfRef, tableMap, throttledHandleCellHover])

    useEffect(() => {
      // makes the controls fade in/out when the mouse is over the table
      const tw = handlesRef.current?.closest(`.${styles.tableWrapper}`)
      if (tw) {
        tw.addEventListener('mouseenter', onMouseEnter)
        tw.addEventListener('mouseleave', onMouseLeave)
        return () => {
          if (tw) {
            tw.removeEventListener('mouseenter', onMouseEnter)
            tw.removeEventListener('mouseleave', onMouseLeave)
          }
        }
      }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Given a rowIndex, select all the cells in that row
     * @param rowIdx the zero-indexed position of the row
     */
    const handleSelectRow = (rowIdx: number) => () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { selection } = view!.state
      if (
        selection instanceof CellSelection &&
        selection.rowSelectionIndex() === rowIdx &&
        tableMap?.getPosBeforeCellList().includes(selection.$headCell.pos)
      ) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        view!.dispatch(_putSelectionAtBeginningOfTable(view!.state, getPos()))
      } else {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectRow(getPos(), rowIdx)(view!.state, view!.dispatch)
      }
      recomputeSelectedCells()
    }
    /**
     * Given a columnIndex, select all the cells in that column
     * @param rowIdx the zero-indexed position of the column
     */
    const handleSelectColumn = (colIdx: number) => () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { selection } = view!.state
      if (
        selection instanceof CellSelection &&
        selection.colSelectionIndex() === colIdx &&
        tableMap?.getPosBeforeCellList().includes(selection.$headCell.pos)
      ) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        view!.dispatch(_putSelectionAtBeginningOfTable(view!.state, getPos()))
      } else {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectColumn(getPos(), colIdx)(view!.state, view!.dispatch)
      }
      recomputeSelectedCells()
    }

    /**
     * Clear all active selections and draw a blue outline around the whole table.
     */
    const handleSelectTable = () => {
      if (tableMap && view) {
        toggleSelectTable(getPos(), view)(view.state, view.dispatch)
      }
    }

    const handleColumnSort = (sortOrder: 'asc' | 'desc', colIdx: number) => {
      if (tableMap && view) {
        if (tableMap.hasVerticalMergedCells()) {
          openCantSortModal()
          return
        }
        view.dispatch(_sortColumn(getPos(), sortOrder, colIdx, view.state))
      }
    }

    const handleAddNewColumn = (colIdx: number) => {
      if (tableMap && view) {
        view.dispatch(_addColumnAt(getPos(), view.state.tr, colIdx))
        if (colIdx === tableMap.numCols) {
          setTimeout(() => {
            scrollRef.current?.scrollBy(Number.MAX_SAFE_INTEGER, 0)
          }, 0)
        }
      }
    }

    const handleAddNewRow = (rowIdx: number) => {
      if (tableMap && view) {
        if (rowIdx === -1) {
          const initialPos = tableMap.getPosAtCoord(0, 0)
          if (initialPos === undefined) return
          addRowAt(initialPos - 1)(view.state, view.dispatch)
        } else {
          const posBeforeLastCellInRow = tableMap.getPosAtCoord(tableMap.numCols - 1, rowIdx)
          if (posBeforeLastCellInRow === undefined) return
          const lastCellNodeSize = view.state.doc.resolve(posBeforeLastCellInRow + 1).parent.nodeSize
          addRowAt(posBeforeLastCellInRow + lastCellNodeSize)(view.state, view.dispatch)
        }
      }
    }

    const handleDragStart = (dragType: 'column' | 'row', startIdx: number) => (e: React.MouseEvent) => {
      const {
        state: { tr },
        dispatch,
      } = view
      e.preventDefault()
      const meta: TableDragManagerMeta = { action: 'startDrag', tablePos: getPos(), dragType, startIdx }
      dispatch(tr.setMeta(TableDragManagerPluginKey, meta))
    }

    const handleResize = (resizeMode: LineMode, rowOrColIdx: number, newSizeOffset: number) => {
      if (view) {
        let newSize: number
        let resizeFn: () => Command
        if (resizeMode === LineMode.Column) {
          resizeFn = () => resizeWidth(rowOrColIdx, newSizeOffset, getPos())

          // Proactively update the scrollbar to avoid jitteryness
          if (columnWidths.length === rowOrColIdx + 1)
            setTableScroll(sum(columnWidths) + newSizeOffset >= (containerDOMRect?.width ?? Infinity))
        } else {
          newSize = rowHeights[rowOrColIdx] + newSizeOffset
          resizeFn = () => resizeHeight(rowOrColIdx, `${Math.round(newSize)}px`, getPos())
        }
        resizeFn()(view.state, view.dispatch)
      }
    }

    /**
     * Given a div.tableContainer, construct a clone that can be used as a preview when dragging
     * @param tableContainerEl the .tableContainer element
     * @returns the preview clone
     */
    const makeTableDragPreviewClone = (tableContainerEl: HTMLDivElement) => {
      const clone = tableContainerEl?.cloneNode(true) as HTMLDivElement
      clone.id = 'dragClone'
      clone.querySelectorAll('.Table_TableHandles__3yJ1o>*:not(.CellBorder_border__3Ysxq)').forEach((el) => {
        el.remove()
      })
      return clone
    }

    const handleDrag = (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()
      const tableContainerEl = (e.target as HTMLElement).closest(`.${styles.tableContainer}`) as HTMLDivElement
      if (view && tableContainerEl) {
        const clone = makeTableDragPreviewClone(tableContainerEl)

        const {
          state: { tr },
          dispatch,
        } = view

        const meta: AlmanacDropCursorPluginMeta = {
          action: 'startDrag',
          draggedNodePos: getPos(),
          targetElementClone: clone,
        }

        dispatch(tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta))
      }
    }

    const tableColumnHandles = useMemo(() => {
      if (!showControls || hoveredColumns.length === 0) return null
      return times(tableMap?.numCols ?? 0, (idx) => (
        <React.Fragment key={`column-${idx}`}>
          {hoveredColumns.includes(idx) && hoveredRows.includes(0) && (
            <div
              className={styles.columnButtonsContainer}
              style={{
                gridColumn: `${(idx + 1) * 2 + 1}`,
              }}
              onMouseEnter={isHoveringControls}
              onMouseLeave={() => {
                setShowSortDropdown(false)
                isNotHoveringControls()
              }}
            >
              <button
                aria-label={`Select column ${idx}`}
                data-test-id={`select-column-${idx}`}
                className={cx(styles.columnOptionsButton, styles.columnGrabber)}
                onMouseDown={(e) => !trackingChanges && handleDragStart('column', idx)(e)}
                onClick={handleSelectColumn(idx)}
              >
                <Grabber />
              </button>
              {!trackingChanges && (
                <ShortcutAwareTooltip label="Sort table">
                  <button
                    className={cx(styles.columnOptionsButton, styles.sortAnchor)}
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    <Sort />
                    {showSortDropdown && (
                      <div className={cx(styles.sortDropdown, { [styles.hide]: !showSortDropdown })}>
                        <button onClick={() => handleColumnSort('asc', idx)}>
                          <div className={styles.sortButtonContent}>
                            <ArrowUp /> Sort ascending
                          </div>
                        </button>
                        <button onClick={() => handleColumnSort('desc', idx)}>
                          <div className={styles.sortButtonContent}>
                            <ArrowDown /> Sort descending
                          </div>
                        </button>
                      </div>
                    )}
                  </button>
                </ShortcutAwareTooltip>
              )}
              <ShortcutAwareTooltip label="Insert a column right">
                <button
                  data-test-id="add-column-between"
                  className={styles.columnOptionsButton}
                  onClick={() => handleAddNewColumn(idx + 1)}
                >
                  <AddIcon />
                </button>
              </ShortcutAwareTooltip>
            </div>
          )}
        </React.Fragment>
      ))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showControls, showSortDropdown, hoveredColumns, hoveredRows, tableMap?.numCols, trackingChanges])

    const colFragments = useMemo(() => tableMap?.getColFragments(), [tableMap])
    const rowFragments = useMemo(() => tableMap?.getRowFragments(), [tableMap])

    const tableVerticalLines = useMemo(() => {
      return dropRight(colFragments, 1) // skip the last column border
        .map((cb, idx) =>
          cb.map(([columnFrom, columnTo]) => (
            <React.Fragment key={`col-border-${idx}-${columnFrom}-${columnTo}`}>
              <div
                className={styles.columnHandle}
                style={{
                  gridColumn: `${(idx + 1) * 2}`,
                  gridRow: `${columnFrom === 0 ? 2 : columnFrom * 2 + 3}/${(columnTo + 1) * 2 + 2}`,
                }}
              />
            </React.Fragment>
          )),
        )
    }, [colFragments])

    const tableHorizontalLines = useMemo(() => {
      return dropRight(rowFragments, 1) // skip the last row border
        .map((rb, idx) => {
          return rb.map(([rowFrom, rowTo]) => (
            <React.Fragment key={`row-border-${idx}-${rowFrom}-${rowTo}`}>
              <div
                className={styles.rowHandle}
                style={{
                  gridRow: `${(idx + 1) * 2}`,
                  gridColumn: `${rowFrom === 0 ? 2 : rowFrom * 2 + 3}/${(rowTo + 1) * 2 + 2}`,
                }}
              />
            </React.Fragment>
          ))
        })
    }, [rowFragments])

    const tableRowHandles = useMemo(() => {
      if (!showControls || hoveredRows.length === 0) return null
      return times(tableMap?.numRows ?? 0, (idx) => (
        <React.Fragment key={`row-${idx}`}>
          {hoveredRows.includes(idx) && hoveredColumns.includes(0) && (
            <div
              className={styles.rowButtonsContainer}
              style={{ gridRow: `${(idx + 1) * 2 + 1}` }}
              onMouseOver={() => setHoveringControls(true)}
            >
              <button
                aria-label={`Select row ${idx}`}
                data-test-id={`select-row-${idx}`}
                onClick={handleSelectRow(idx)}
                className={cx(styles.iconBtn, styles.rowOptionsButton)}
                onMouseDown={(e) => {
                  if (trackingChanges || rowIsHeaderCells(getPos(), idx, view.state.doc)) return
                  handleDragStart('row', idx)(e)
                }}
              >
                <Grabber />
              </button>
              {rowIsHeaderCells(getPos(), idx, view.state.doc) ? (
                <ShortcutAwareTooltip label="Unpin header up to this row">
                  <button
                    className={styles.rowOptionsButton}
                    onClick={() => {
                      const didConvert = removeHeadersUpToRow(getPos(), idx)(view.state, view.dispatch)
                      if (!didConvert) {
                        openModal(
                          NotificationModal,
                          {
                            title: 'There was a problem',
                            message:
                              'You cannot unpin a row that has vertically merged cells. Split the merged cells and try again.',
                          },
                          { newStack: false },
                        )
                      }
                    }}
                  >
                    <Unpin />
                  </button>
                </ShortcutAwareTooltip>
              ) : (
                <ShortcutAwareTooltip label="Pin header up to this row">
                  <button
                    className={styles.rowOptionsButton}
                    onClick={() => {
                      const didConvert = convertRowsToHeader(getPos(), idx)(view.state, view.dispatch)
                      if (!didConvert) {
                        openModal(
                          NotificationModal,
                          {
                            title: 'There was a problem',
                            message:
                              'You cannot pin a row that has vertically merged cells. Split the merged cells and try again.',
                          },
                          { newStack: false },
                        )
                      }
                    }}
                  >
                    <Pin />
                  </button>
                </ShortcutAwareTooltip>
              )}
              <ShortcutAwareTooltip label="Insert a row below">
                <button
                  data-test-id="add-row-between"
                  className={styles.rowOptionsButton}
                  onClick={() => handleAddNewRow(idx)}
                >
                  <AddIcon />
                </button>
              </ShortcutAwareTooltip>
            </div>
          )}
        </React.Fragment>
      ))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showControls, view.state, hoveredColumns, hoveredRows, tableMap?.numRows, trackingChanges])

    const isNewTableMenuEnabled = useFlipperFlag('new_table_menu')
    const tableContextMenu = isNewTableMenuEnabled ? (
      <TableContextMenu trackingChanges={trackingChanges} view={view} />
    ) : undefined

    return (
      <div
        className={cx(styles.tableWrapper, {
          [styles.hasSelection]: selectedCells.length > 0,
          [styles.denseTable]: dense_table,
        })}
        ref={selfRef}
      >
        <TableScrollIndicator containerRef={selfRef} scrollRef={scrollRef} />
        <div ref={scrollRef} className={cx(styles.tableContainer, { [styles.tableScroll]: tableScroll })}>
          <div className={styles.TableHandlesContainer}>
            <div
              className={styles.TableHandles}
              style={{
                gridTemplateColumns: gridColumnSizes()
                  .map((cs) => `${cs}px`)
                  .join(' '),
                gridTemplateRows: gridRowSizes()
                  .map((cs) => `${cs}px`)
                  .join(' '),
              }}
              ref={handlesRef}
            >
              {view && tableMap && <CellBorder view={view} getPos={getPos} node={node} />}
              {showControls && tableColumnHandles}
              {tableVerticalLines}
              {tableHorizontalLines}
              {showControls && tableRowHandles}
              {showControls &&
                (isNewTableMenuEnabled ? (
                  <MenuTrigger isDisabled={!isNewTableMenuEnabled} placement="right-end">
                    <TableButton
                      isTableSelected={isTableSelected}
                      onClick={handleSelectTable}
                      onDragStart={handleDrag}
                      onPointerLeave={resetTimeout}
                    />
                    {tableContextMenu ?? <div />}
                  </MenuTrigger>
                ) : (
                  <div
                    className={cx(styles.tableHandle, isTableSelected ? styles.active : styles.inactive)}
                    onClick={handleSelectTable}
                    onDragStart={handleDrag}
                    draggable={showControls}
                  >
                    <TableIcon />
                  </div>
                ))}
              {showControls && (
                <button
                  className={cx(styles.iconBtn, styles.addNewColumn, { [styles.show]: lastColumnHovered })}
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  onClick={() => handleAddNewColumn(tableMap!.numCols)}
                >
                  <AddIcon />
                </button>
              )}
              {showControls && (
                <button
                  className={cx(styles.iconBtn, styles.addNewRow, { [styles.show]: lastRowHovered })}
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  onClick={() => handleAddNewRow(tableMap!.numRows - 1)}
                >
                  <AddIcon />
                </button>
              )}
              {/* <div className={cx(styles.tableResizeHandle, { [styles.hidden]: !isTableSelected })}>
              <TableResizeIcon />
            </div> */}
              {showControls && isTableSelected && <div className={styles.tableSelectionIndicator} />}
              {showControls && <CellSelectionIndicator selectedCells={selectedCells} />}
              {showControls && tableMap && (
                <CellResizeHandler
                  getPos={getPos}
                  columnWidths={columnWidths}
                  rowHeights={rowHeights}
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  rowBorders={rowFragments!}
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  columnBorders={colFragments!}
                  handleSelectBorder={(...args) => {
                    selectBorder(...args)(view.state, view.dispatch)
                  }}
                  handleResize={handleResize}
                  selection={view.state.selection instanceof BorderSelection ? view.state.selection : undefined}
                  tableRef={contentDomRef as React.RefObject<HTMLTableElement>}
                  containerRef={selfRef}
                  setShowSnapBorder={setShowSnapBorder}
                />
              )}
            </div>
            <div className={cx(styles.TableSnapBorder, { [styles.visible]: showSnapBorder })} />
          </div>
          <table spellCheck={false} ref={contentDomRef} style={{ background: node?.attrs.background }} />
          {children}
        </div>
      </div>
    )
  },
)
